/**
 * OccurrencePatternsTab Component
 *
 * Clusters reports by identical barrier failure signatures to surface
 * recurring fault tree failure patterns across different incidents.
 *
 * For each MAPPED report, a signature is built from its failed barriers
 * (e.g. "B1,B12"). Reports sharing the same failed barriers are grouped
 * into clusters, sorted by frequency descending. Successful or unmapped
 * barriers are excluded from the signature.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import GridOnIcon from '@mui/icons-material/GridOn';
import ViewListIcon from '@mui/icons-material/ViewList';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TimelineIcon from '@mui/icons-material/Timeline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    TimeScale,
    Tooltip as ChartTooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import {Chart, Scatter} from 'react-chartjs-2';
import {
    useBarrierAnalysisCache, useIRPWorkbookStore, usePendingClusterSearch, usePrimaryFilter,
} from '../../../../../../../stores/irp-workbook.store';
import {FaultTreeNode} from '../../../../../../../@types/faultTreeNode';
import {IrpBarrierReportDTO} from '../../../../../../../@types/integrated-risk-picture';
import {useScrollIntoViewOnActivate} from '../../../../../../../hooks/useScrollIntoViewOnActivate';
import {useIRPContext} from '../../IRPWorkbookProvider';
import {thresholdLinesPlugin} from '../../../../../../../components/workspace/modals/canvas/ParetoChart.component';
import {scatterDiagonalPlugin} from '../shared/scatterDiagonalPlugin';
import {TrendWindow, TrendWindowSelect} from '../shared/TrendWindowSelect';
import {KpiCard} from '../shared/KpiCard';
import {useEscapeKey} from '../../hooks/useEscapeKey';
import {FULLSCREEN_OVERLAY_SX, FullscreenToggle} from '../shared/Fullscreen';
import {TrendWindowBoundary, computeTrendWindowBoundary} from '../../utils/trendWindowBoundary';
import {CountTrendStats, computeCountTrend, formatTrendStats, laplaceTrend} from '../../utils/temporalDistribution';
import {WeibullFit, fitWeibullMLE} from '../../utils/weibull';
import {CrowAmsaaFit, fitCrowAmsaa} from '../../utils/crowAmsaa';
import {fiveNumberSummary} from '../../utils/distributionStats';
import {GapDistData, TimeToFailureDialog} from '../shared/TimeToFailureDialog';
import TemporalDistributionPanel, {TemporalEntity, TemporalSort} from './occurrence-patterns/TemporalDistributionPanel';

ChartJS.register(CategoryScale, LinearScale, TimeScale, BarElement, LineElement, PointElement, ChartTooltip, Legend, Filler);

interface BarrierOutcome {
    nodeId: number;
    uniqueId: string;
    name: string;
    outcome: 'SUCCESS' | 'FAILURE';
}

interface ClusterReport {
    id: number;
    uniqueId: string;
    dateTime?: Date;
    sourceName?: string;
}

interface OccurrenceCluster {
    signatureKey: string;
    barriers: BarrierOutcome[];
    reports: ClusterReport[];
    count: number;
}

interface ScatterReport {
    uniqueId: string;
    clusterIdx: number;
    failedBarriers: string[];
}

interface ScatterGroup {
    x: number;
    y: number;
    reports: ScatterReport[];
    dominantCluster: number;
}

interface HeatmapResult {
    barriers: {id: number; uniqueId: string; name: string}[];
    matrix: number[][];
    maxCount: number;
}

/** Off-thread clustering output, consumed directly (Dates preserved by structured clone). */
interface ClusteringResult {
    clusters: OccurrenceCluster[];
    heatmapData: HeatmapResult;
    scatterGroups: ScatterGroup[];
}

const EMPTY_CLUSTERS: OccurrenceCluster[] = [];
const EMPTY_SCATTER: ScatterGroup[] = [];
const EMPTY_HEATMAP: HeatmapResult = {barriers: [], matrix: [], maxCount: 1};

/** Above this report count, expanding a cluster paints a spinner first to avoid a perceived freeze. */
const EXPAND_SPINNER_THRESHOLD = 50;

/** `'midpoint'` splits the full date range (IRP filter when set, else data min→max) in half
 *  and compares the second half against the first; numeric values are months;
 *  `'allTime'` turns the trend lens OFF — no recent/prior comparison, no overlay,
 *  no rising/falling chips. Matches the Barrier Effectiveness option of the same name. */
type TrendWindowOption = TrendWindow;
/** Default trend window — All Time shows the complete, unsplit picture first; the
 *  user can opt into a recent-vs-prior trend lens via the dropdown. */
const DEFAULT_TREND_WINDOW: TrendWindowOption = 'allTime';

type ClusterSubTab = 'summary' | 'charts' | 'clusters';

const OccurrencePatternsTab: React.FC = () => {
    const {irpId, dataSource} = useIRPContext();
    const irpDetails = useIRPWorkbookStore((state) => state.irpDetails);
    const eventSequenceData = useIRPWorkbookStore((state) => state.eventSequenceData);
    const primaryFilter = usePrimaryFilter();
    const barrierAnalysisCache = useBarrierAnalysisCache();
    const setBarrierAnalysisCache = useIRPWorkbookStore((state) => state.setBarrierAnalysisCache);
    const navigateToReportAnalysis = useIRPWorkbookStore((state) => state.navigateToReportAnalysis);
    const pendingClusterSearch = usePendingClusterSearch();
    const setPendingClusterSearch = useIRPWorkbookStore((state) => state.setPendingClusterSearch);

    const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());
    // Hover sync: tracks which cluster is currently under the cursor in either the
    // chart or the list. Setting it from either side dims the other side's mismatches.
    const [hoveredClusterKey, setHoveredClusterKey] = useState<string | null>(null);
    const [pendingExpandKey, setPendingExpandKey] = useState<string | null>(null);
    const [clusterReportPage, setClusterReportPage] = useState<Map<string, number>>(new Map());
    const [clusterReportsPerPage, setClusterReportsPerPage] = useState(10);
    const [scrollToClusterKey, setScrollToClusterKey] = useState<string | null>(null);
    const [clusterSearch, setClusterSearch] = useState('');
    const [trendWindowMonths, setTrendWindowMonths] = useState<TrendWindowOption>(DEFAULT_TREND_WINDOW);
    const [chartMode, setChartMode] = useState<'pareto' | 'scatter' | 'temporal' | 'heatmap' | 'source'>('temporal');
    const [chartHeight, setChartHeight] = useState(300);
    const [clusterPage, setClusterPage] = useState(0);
    const [clustersPerPage, setClustersPerPage] = useState(10);
    const [paretoLimit, setParetoLimit] = useState(50);
    const [temporalSort, setTemporalSort] = useState<TemporalSort>('count');
    // "Min in window" threshold — hide clusters whose count INSIDE the trend window
    // (recent + prior halves) is below this. Linked to the Window selector: changing
    // the window changes what counts, so the filter follows the analyst's current
    // period of interest. Shared by chart and list. Default 1 = show everything.
    const [minClusterCount, setMinClusterCount] = useState(1);
    const [sourceLimit, setSourceLimit] = useState(50);
    const [chartFullscreen, setChartFullscreen] = useState(false);
    const [topLevelOnly, setTopLevelOnly] = useState(true);
    // Mutually-exclusive sub-tabs (mirrors the Barrier Effectiveness restructure):
    // Summary = clustering summary cards, Charts = the distribution charts, Clusters
    // = the paginated cluster list. Default Summary.
    const [activeSubTab, setActiveSubTab] = useState<ClusterSubTab>('summary');
    const [newClusterMonths, setNewClusterMonths] = useState(3);
    // Clusters sub-tab "New only" filter — restricts the list to newly-emerging
    // clusters (all reports within the last `newClusterMonths`). Driven from the
    // Dashboard's New Clusters KPI card, which navigates here with this toggle on.
    const [newOnly, setNewOnly] = useState(false);
    // Time-to-failure drill-down: the cluster whose inter-occurrence gap distribution
    // is being inspected (null = dialog closed), plus its Weibull-overlay toggle and
    // forecast horizon. Mirrors the Barrier Effectiveness MTBF drill-down.
    const [gapDistCluster, setGapDistCluster] = useState<OccurrenceCluster | null>(null);
    const [showClusterWeibull, setShowClusterWeibull] = useState(false);
    const [clusterPredictionDays, setClusterPredictionDays] = useState(30);

    // Heavy clustering runs off-thread in a Web Worker. `analysis` holds the results.
    const workerRef = useRef<Worker | null>(null);
    const reqIdRef = useRef(0);
    const detailedAnalysisRef = useRef<HTMLDivElement>(null);
    const chartPanelRef = useRef<HTMLDivElement>(null);
    const [analysis, setAnalysis] = useState<ClusteringResult | null>(null);

    // Escape key exits fullscreen
    useEscapeKey(chartFullscreen, (): void => setChartFullscreen(false));

    // When the detailed analysis is activated, scroll it into view so it isn't hidden
    // below the executive summary that may fill the viewport.
    useScrollIntoViewOnActivate(detailedAnalysisRef, activeSubTab === 'clusters');

    // Cross-tab navigation seed: when BE clicks a barrier, the store carries a
    // pending search term (e.g. "LOC-Ia1.1.2") through `pendingClusterSearch`. We
    // consume it once by populating the cluster-search field and opening the detailed
    // view, then clear it so the user can type freely afterwards. Reuses the existing
    // search UX instead of adding a separate filter chip.
    useEffect(() => {
        if (pendingClusterSearch == null) return;
        setClusterSearch(pendingClusterSearch);
        setClusterPage(0);
        setActiveSubTab('clusters');
        setPendingClusterSearch(null);
    }, [pendingClusterSearch, setPendingClusterSearch]);

    // When the Charts sub-tab is activated (or the chart type switched), scroll the chart
    // panel into view — its type-selector icons live in the sticky header, so the panel
    // itself sits lower.
    useEffect(() => {
        if (activeSubTab !== 'charts' || chartFullscreen) return;
        const id = requestAnimationFrame(() => {
            chartPanelRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
        });
        return (): void => cancelAnimationFrame(id);
    }, [activeSubTab, chartMode, chartFullscreen]);

    // Create the clustering worker once; tear it down on unmount.
    useEffect(() => {
        const worker = new Worker(new URL('../../../../../../../utils/report-clustering.worker.ts', import.meta.url));
        worker.onmessage = (e: MessageEvent): void => {
            const {requestId, result} = e.data as {requestId: number; result: ClusteringResult};
            if (requestId !== reqIdRef.current) return; // ignore stale responses
            setAnalysis(result);
        };
        workerRef.current = worker;
        return (): void => {
            worker.terminate();
            workerRef.current = null;
        };
    }, []);

    const handleChartResizeStart = useCallback((e: React.MouseEvent): void => {
        e.preventDefault();
        const startY = e.clientY;
        const startH = chartHeight;

        // Create an invisible overlay that covers the entire viewport during drag
        // This captures all mouse events and enforces the resize cursor
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;cursor:row-resize;z-index:9999;';
        document.body.appendChild(overlay);

        const onMouseMove = (moveEvent: MouseEvent): void => {
            setChartHeight(Math.max(100, startH + moveEvent.clientY - startY));
        };

        const onMouseUp = (): void => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.removeChild(overlay);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [chartHeight]);

    useEffect(() => {
        if (!dataSource || barrierAnalysisCache !== null) return;
        const descriptorIds = primaryFilter.selectedDescriptors.size > 0
            ? Array.from(primaryFilter.selectedDescriptors)
            : null;
        dataSource.getWorkbookBarrierAnalysis(primaryFilter.dateFrom, primaryFilter.dateTo, descriptorIds)
            .then(setBarrierAnalysisCache)
            .catch(() => { /* errors surfaced via global handler */ });
    }, [dataSource, barrierAnalysisCache, primaryFilter, setBarrierAnalysisCache]);

    // Extract fault tree nodes
    const faultTreeNodes = useMemo((): FaultTreeNode[] => {
        const initiatingEvent = eventSequenceData?.events?.[0];
        const faultTree = initiatingEvent?.faultTree;
        return faultTree?.faultTreeNodes || [];
    }, [eventSequenceData]);

    // Compute fault tree depth for each node (walk up parentId to root)
    const nodeDepthMap = useMemo((): Map<number, number> => {
        const nodeById = new Map<number, FaultTreeNode>();
        faultTreeNodes.forEach((n) => nodeById.set(n.id, n));

        const depthCache = new Map<number, number>();
        function getDepth(nodeId: number): number {
            if (depthCache.has(nodeId)) return depthCache.get(nodeId)!;
            const node = nodeById.get(nodeId);
            if (!node || node.parentId == null || !nodeById.has(node.parentId)) {
                depthCache.set(nodeId, 0);
                return 0;
            }
            const d = getDepth(node.parentId) + 1;
            depthCache.set(nodeId, d);
            return d;
        }
        faultTreeNodes.forEach((n) => getDepth(n.id));
        return depthCache;
    }, [faultTreeNodes]);

    // Get all barrier nodes sorted by depth descending (deepest first), then uniqueId
    const barrierNodes = useMemo((): FaultTreeNode[] => {
        return faultTreeNodes
            .filter((n) => n.nodeClass === 'BARRIER')
            .sort((a, b) => {
                const depthDiff = (nodeDepthMap.get(b.id) ?? 0) - (nodeDepthMap.get(a.id) ?? 0);
                if (depthDiff !== 0) return depthDiff;
                return (a.uniqueId || '').localeCompare(b.uniqueId || '');
            });
    }, [faultTreeNodes, nodeDepthMap]);

    // Identify child barriers: barriers whose ancestor chain contains another barrier
    const childBarrierIds = useMemo((): Set<number> => {
        const barrierIdSet = new Set(barrierNodes.map((b) => b.id));
        const nodeById = new Map<number, FaultTreeNode>();
        faultTreeNodes.forEach((n) => nodeById.set(n.id, n));
        const childIds = new Set<number>();
        for (const barrier of barrierNodes) {
            let current = barrier.parentId != null ? nodeById.get(barrier.parentId) : undefined;
            while (current) {
                if (barrierIdSet.has(current.id)) {
                    childIds.add(barrier.id);
                    break;
                }
                current = current.parentId != null ? nodeById.get(current.parentId) : undefined;
            }
        }
        return childIds;
    }, [barrierNodes, faultTreeNodes]);

    // Barrier nodes used for clustering (optionally excluding child barriers)
    const clusterBarrierNodes = useMemo((): FaultTreeNode[] => {
        if (!topLevelOnly) return barrierNodes;
        return barrierNodes.filter((b) => !childBarrierIds.has(b.id));
    }, [barrierNodes, topLevelOnly, childBarrierIds]);

    // BFF returns only MAPPED reports — use directly (kept on the main thread for
    // total-count denominators and the empty-state guard).
    const mappedReports = useMemo((): IrpBarrierReportDTO[] => {
        if (!barrierAnalysisCache) return [];
        return barrierAnalysisCache.reports;
    }, [barrierAnalysisCache]);

    // Send the resident dataset to the worker whenever the report set changes.
    useEffect(() => {
        const worker = workerRef.current;
        if (!worker || !barrierAnalysisCache) return;
        worker.postMessage({type: 'init', reports: barrierAnalysisCache.reports});
    }, [barrierAnalysisCache]);

    // Recompute whenever the dataset or the active (top-level-filtered) barrier set changes.
    useEffect(() => {
        const worker = workerRef.current;
        if (!worker || !barrierAnalysisCache) return;
        const requestId = ++reqIdRef.current;
        worker.postMessage({
            type: 'compute',
            requestId,
            clusterBarrierNodes: clusterBarrierNodes.map((b) => ({id: b.id, uniqueId: b.uniqueId ?? '', name: b.name ?? ''})),
        });
    }, [barrierAnalysisCache, clusterBarrierNodes]);

    // Worker-derived clustering (null until the first compute returns).
    const clusters = analysis?.clusters ?? EMPTY_CLUSTERS;

    // Reset pagination when the underlying clusters change. Search-driven resets are
    // handled in the search field handlers so the chart click can clear the search
    // without our effect fighting its own setClusterPage(targetPage).
    useEffect(() => {
        setClusterPage(0);
    }, [clusters]);

    // Scroll to a cluster card after the click-to-expand flow has navigated/expanded it.
    // We wait one rAF so the (possibly new-page) Paper is in the DOM before scrolling.
    useEffect(() => {
        if (!scrollToClusterKey) return undefined;
        const id = requestAnimationFrame(() => {
            const el = document.querySelector<HTMLElement>(`[data-cluster-key="${CSS.escape(scrollToClusterKey)}"]`);
            el?.scrollIntoView({behavior: 'smooth', block: 'start'});
            setScrollToClusterKey(null);
        });
        return (): void => cancelAnimationFrame(id);
    }, [scrollToClusterKey, clusterPage]);

    // Trend window boundaries — anchors to the IRP's To-Date filter when set (matches the
    // user's mental model of the analysis window); falls back to the max report date.
    // `start` is the prior-window's lower bound; `end` is the reference "now".
    // In `'midpoint'` mode `start`/`end` span the full IRP filter range (or data min→max
    // when the filter is unset) and `recentStart` sits exactly at the midpoint.
    // When the user picks 'All time', any trend-based sort would silently degrade
    // (all clusters have null trend → comparator falls back to count desc). Snap
    // the sort back to 'count' so the dropdown shows the active state honestly.
    useEffect(() => {
        if (trendWindowMonths !== 'allTime') return;
        if (temporalSort === 'rising-first' || temporalSort === 'most-accelerating' || temporalSort === 'falling-first') {
            setTemporalSort('count');
        }
    }, [trendWindowMonths, temporalSort]);

    const trendWindow = useMemo((): TrendWindowBoundary | null => {
        return computeTrendWindowBoundary(
            trendWindowMonths,
            primaryFilter.dateFrom ? primaryFilter.dateFrom.getTime() : null,
            primaryFilter.dateTo ? primaryFilter.dateTo.getTime() : null,
            () => clusters.flatMap((cluster) => cluster.reports.map((r) => (r.dateTime ? r.dateTime.getTime() : NaN))),
        );
    }, [clusters, primaryFilter.dateFrom, primaryFilter.dateTo, trendWindowMonths]);

    // Trend, raw counts, and ratio per cluster. Raw counts back the tooltip stats so the
    // ↑↓→ verdict is auditable; the ratio encodes edge cases (`+Infinity` for emerging,
    // `0` for winding down, `NaN` for no signal) so the "Most accelerating" sort works
    // with a single descending comparison.
    const clusterTrendStats = useMemo((): Map<string, CountTrendStats> => {
        const stats = new Map<string, CountTrendStats>();
        clusters.forEach((cluster) => {
            const events = cluster.reports
                .filter((r) => r.dateTime != null && !isNaN(r.dateTime.getTime()))
                .map((r) => r.dateTime!.getTime());
            stats.set(cluster.signatureKey, computeCountTrend(events, trendWindow));
        });
        return stats;
    }, [clusters, trendWindow]);

    // Each cluster's rank in the count-sorted master list — used to label Dotplot rows
    // (C1, C2…) by their identity rather than by their position in the current chart sort.
    const clusterCountRank = useMemo((): Map<string, number> => {
        const rank = new Map<string, number>();
        clusters.forEach((c, i) => rank.set(c.signatureKey, i));
        return rank;
    }, [clusters]);

    // Inter-occurrence gap distribution for the drill-down cluster. Mirrors the Barrier
    // Effectiveness MTBF drill-down, but over the cluster's report dates: the gaps between
    // consecutive occurrences expose the full shape behind the mean (MTBF), so the analyst
    // can judge whether the cluster recurs in tight bursts or at a steady cadence.
    const gapDistData = useMemo((): GapDistData | null => {
        if (!gapDistCluster) return null;
        const occurrenceTs: number[] = [];
        for (const r of gapDistCluster.reports) {
            if (!r.dateTime) continue;
            const t = r.dateTime.getTime();
            if (isNaN(t)) continue;
            occurrenceTs.push(t);
        }
        occurrenceTs.sort((a, b) => a - b);
        if (occurrenceTs.length < 2) return null;
        // Consecutive gaps in days.
        const MS_PER_DAY = 86400000;
        const gaps: number[] = [];
        for (let i = 1; i < occurrenceTs.length; i++) {
            gaps.push((occurrenceTs[i] - occurrenceTs[i - 1]) / MS_PER_DAY);
        }
        gaps.sort((a, b) => a - b);
        const stats = fiveNumberSummary(gaps);
        // Sturges' formula for bin count; bins start at min (not 0) — copy of the BE binning.
        const binCount = Math.max(1, Math.ceil(Math.log2(gaps.length) + 1));
        const range = stats.max - stats.min || 1;  // guard against single-value-repeated edge case
        const binWidth = range / binCount;
        const bins: {start: number; end: number; count: number}[] = [];
        for (let i = 0; i < binCount; i++) {
            bins.push({start: stats.min + i * binWidth, end: stats.min + (i + 1) * binWidth, count: 0});
        }
        for (const g of gaps) {
            const idx = Math.min(Math.floor((g - stats.min) / binWidth), binCount - 1);
            bins[idx].count++;
        }
        const cdfPoints = gaps.map((g, i) => ({x: g, y: ((i + 1) / gaps.length) * 100}));
        return {gaps, bins, cdfPoints, stats};
    }, [gapDistCluster]);

    // Opt-in Weibull MLE fit over the cluster's inter-occurrence gaps (drives the overlay).
    const weibullFit = useMemo((): WeibullFit | null => {
        return showClusterWeibull && gapDistData ? fitWeibullMLE(gapDistData.gaps) : null;
    }, [showClusterWeibull, gapDistData]);

    // Crow-AMSAA (power-law NHPP) reliability-growth fit for the drill-down cluster — the
    // trend-model companion to the Weibull-on-gaps view. Every report IS an occurrence.
    // Observation window = the analysis date range when set, else the full span of report
    // dates across ALL clusters. Auto-opens the "growth" tab on a MEANINGFUL trend (β
    // outside the 0.9–1.1 "random" band) to stay consistent with the regime chip.
    const crowAmsaa = useMemo((): {fit: CrowAmsaaFit | null; initialTab: 'gap' | 'growth'} => {
        if (!gapDistCluster) return {fit: null, initialTab: 'gap'};
        const occurrenceTs: number[] = [];
        for (const r of gapDistCluster.reports) {
            if (!r.dateTime) continue;
            const t = r.dateTime.getTime();
            if (isNaN(t)) continue;
            occurrenceTs.push(t);
        }
        let minMs = Infinity;
        let maxMs = -Infinity;
        for (const cl of clusters) {
            for (const r of cl.reports) {
                if (!r.dateTime) continue;
                const t = r.dateTime.getTime();
                if (isNaN(t)) continue;
                if (t < minMs) minMs = t;
                if (t > maxMs) maxMs = t;
            }
        }
        const originMs = primaryFilter.dateFrom?.getTime() ?? minMs;
        const endMs = primaryFilter.dateTo?.getTime() ?? maxMs;
        if (!isFinite(originMs) || !isFinite(endMs)) return {fit: null, initialTab: 'gap'};
        const fit = fitCrowAmsaa(occurrenceTs, originMs, endMs);
        // For now, always open on the Gap distribution tab (predictable landing while
        // Crow-AMSAA beds in). To restore effect-size auto-select, replace the line below:
        //   const meaningfulTrend = fit != null && (fit.beta < 0.9 || fit.beta > 1.1);
        //   return {fit, initialTab: meaningfulTrend ? 'growth' : 'gap'};
        return {fit, initialTab: 'gap'};
    }, [gapDistCluster, clusters, primaryFilter.dateFrom, primaryFilter.dateTo]);

    // Comparator implementing the current sort selection. `null` for the default 'count'
    // sort — both the chart and the cluster list short-circuit to the already-count-sorted
    // `clusters` array in that case. Shared by the Dotplot AND the cluster list so they
    // always display in the same order.
    const temporalSortComparator = useMemo((): ((a: OccurrenceCluster, b: OccurrenceCluster) => number) | null => {
        if (temporalSort === 'count') return null;
        if (temporalSort === 'most-accelerating') {
            // Only rising-trend clusters (ratio > 1.3) are genuinely accelerating. Stable
            // clusters with ratio ≈ 1.0 used to leak to the top of this sort just because
            // their ratio outranked falling clusters — that surprised the user. Now: all
            // rising clusters come first (sorted by ratio desc, with emerging ratio=∞ at
            // the top), and everything else falls below sorted by count desc.
            return (a, b): number => {
                const sa = clusterTrendStats.get(a.signatureKey);
                const sb = clusterTrendStats.get(b.signatureKey);
                const aRising = sa?.trend === 'rising';
                const bRising = sb?.trend === 'rising';
                if (aRising !== bRising) return aRising ? -1 : 1;
                if (!aRising) return b.count - a.count;
                const ra = sa?.ratio ?? NaN;
                const rb = sb?.ratio ?? NaN;
                if (ra === rb) return b.count - a.count;
                return rb - ra;
            };
        }
        const order: Record<string, number> = temporalSort === 'rising-first'
            ? {rising: 0, stable: 1, falling: 2}
            : {falling: 0, stable: 1, rising: 2};
        return (a, b): number => {
            const ta = clusterTrendStats.get(a.signatureKey)?.trend ?? null;
            const tb = clusterTrendStats.get(b.signatureKey)?.trend ?? null;
            const ra = ta == null ? 3 : (order[ta] ?? 3);
            const rb = tb == null ? 3 : (order[tb] ?? 3);
            if (ra !== rb) return ra - rb;
            return b.count - a.count;
        };
    }, [temporalSort, clusterTrendStats]);

    // Adapter feeding the Temporal Distribution panel. Mirrors the old
    // sortedClustersForTemporal → filteredClustersForTemporal pipeline MINUS the
    // trend-chip filter and the "Show top N" range slice (the panel does those):
    //   1. sort by the shared comparator (same order as the cluster list);
    //   2. drop clusters below the "Min in window" threshold (window mode only);
    //   3. map each cluster to the generic TemporalEntity contract.
    const temporalEntities = useMemo((): TemporalEntity[] => {
        let source = temporalSortComparator ? [...clusters].sort(temporalSortComparator) : clusters;
        if (minClusterCount > 1 && trendWindow) {
            source = source.filter((c) => {
                const s = clusterTrendStats.get(c.signatureKey);
                return ((s?.recent ?? 0) + (s?.prior ?? 0)) >= minClusterCount;
            });
        }
        return source.map((c): TemporalEntity => {
            const rank = clusterCountRank.get(c.signatureKey);
            const events = c.reports
                .filter((r) => r.dateTime != null && !isNaN(r.dateTime.getTime()))
                .map((r) => r.dateTime!.getTime());
            return {
                key: c.signatureKey,
                label: rank == null ? '' : `C${rank + 1}`,
                title: c.barriers.map((b) => b.uniqueId).join(', '),
                events,
                count: c.count,
                trend: clusterTrendStats.get(c.signatureKey) ?? {trend: null, recent: 0, prior: 0, ratio: NaN},
            };
        });
    }, [clusters, temporalSortComparator, minClusterCount, trendWindow, clusterTrendStats, clusterCountRank]);

    // New clusters: clusters where ALL reports fall within the last N months —
    // i.e. failure patterns that have only just started appearing. Surfaced as the
    // Dashboard's New Clusters KPI and as the Clusters sub-tab "New only" filter.
    const newClusters = useMemo((): Array<{index: number; cluster: OccurrenceCluster}> => {
        const cutoff = new Date();
        cutoff.setMonth(cutoff.getMonth() - newClusterMonths);
        const cutoffTime = cutoff.getTime();
        const result: Array<{index: number; cluster: OccurrenceCluster}> = [];
        clusters.forEach((cluster, idx) => {
            const allRecent = cluster.reports.length > 0 && cluster.reports.every((r) => {
                if (!r.dateTime) return false;
                return new Date(r.dateTime).getTime() >= cutoffTime;
            });
            if (allRecent) result.push({index: idx, cluster});
        });
        return result;
    }, [clusters, newClusterMonths]);

    // Signature keys of the new clusters — used to filter the list when "New only" is on.
    const newClusterKeys = useMemo((): Set<string> => {
        return new Set(newClusters.map(({cluster}) => cluster.signatureKey));
    }, [newClusters]);

    // Cluster list view: search filter applied first, then the same `temporalSort`
    // comparator the Dotplot uses — list and chart always display in the same order.
    // The search box accepts multiple barrier terms separated by commas or whitespace;
    // a cluster matches only when EVERY term hits at least one of its barriers
    // (case-insensitive substring on either uniqueId or name). Empty terms are ignored
    // so trailing commas / extra spaces don't accidentally hide everything.
    const filteredClusters = useMemo((): OccurrenceCluster[] => {
        const terms = clusterSearch
            .split(/[,\s]+/)
            .map((t) => t.trim().toLowerCase())
            .filter((t) => t.length > 0);
        let filtered = terms.length === 0
            ? clusters
            : clusters.filter((c) => terms.every((term) =>
                c.barriers.some((b) =>
                    (b.uniqueId ?? '').toLowerCase().includes(term) ||
                    (b.name ?? '').toLowerCase().includes(term),
                ),
            ));
        if (newOnly) {
            filtered = filtered.filter((c) => newClusterKeys.has(c.signatureKey));
        }
        if (minClusterCount > 1 && trendWindow) {
            filtered = filtered.filter((c) => {
                const s = clusterTrendStats.get(c.signatureKey);
                return ((s?.recent ?? 0) + (s?.prior ?? 0)) >= minClusterCount;
            });
        }
        if (!temporalSortComparator) return filtered;
        return [...filtered].sort(temporalSortComparator);
    }, [clusters, clusterSearch, newOnly, newClusterKeys, temporalSortComparator, minClusterCount, trendWindow, clusterTrendStats]);

    const toggleCluster = (signatureKey: string, reportCount: number): void => {
        const isCurrentlyExpanded = expandedClusters.has(signatureKey);
        if (isCurrentlyExpanded || reportCount < EXPAND_SPINNER_THRESHOLD) {
            setExpandedClusters((prev) => {
                const next = new Set(prev);
                if (next.has(signatureKey)) next.delete(signatureKey);
                else next.add(signatureKey);
                return next;
            });
            return;
        }
        // Large expansion: paint the spinner first, then mount the table. Double rAF ensures
        // the spinner has actually rendered (frame N painted) before the blocking mount runs.
        setPendingExpandKey(signatureKey);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setExpandedClusters((prev) => new Set(prev).add(signatureKey));
                setPendingExpandKey(null);
            });
        });
    };

    const formatDate = (dateTime?: Date): string => {
        if (!dateTime) return '—';
        const d = new Date(dateTime);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});
    };

    const totalReportsInClusters = clusters.reduce((sum, c) => sum + c.count, 0);

    const paretoClusters = useMemo(
        (): OccurrenceCluster[] => clusters.slice(0, paretoLimit),
        [clusters, paretoLimit],
    );

    const paretoData = useMemo(() => {
        // Cumulative percentage (relative to ALL clusters, not just truncated)
        const cumulative: number[] = [];
        let runningTotal = 0;
        for (const c of paretoClusters) {
            runningTotal += c.count;
            cumulative.push(totalReportsInClusters > 0
                ? Math.round((runningTotal / totalReportsInClusters) * 100)
                : 0);
        }
        return {
            labels: paretoClusters.map((_, i) => `C${i + 1}`),
            datasets: [
                {
                    type: 'bar' as const,
                    data: paretoClusters.map((c) => c.count),
                    backgroundColor: '#ef5350',
                    yAxisID: 'y',
                    order: 2,
                },
                {
                    type: 'line' as const,
                    data: cumulative,
                    borderColor: '#1976d2',
                    backgroundColor: '#1976d2',
                    borderWidth: 2,
                    pointRadius: 3,
                    tension: 0,
                    yAxisID: 'y1',
                    order: 1,
                },
            ],
        };
    }, [paretoClusters, totalReportsInClusters]);

    const paretoOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {display: false},
            tooltip: {
                callbacks: {
                    title: (items: { dataIndex: number }[]): string => {
                        const idx = items[0]?.dataIndex;
                        if (idx == null || !paretoClusters[idx]) return '';
                        return paretoClusters[idx].barriers.map((b) => b.uniqueId).join(', ');
                    },
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    label: (item: any): string => {
                        if (item.dataset?.type === 'line') return `Cumulative: ${item.parsed.y}%`;
                        return `${item.parsed.y} report${item.parsed.y !== 1 ? 's' : ''}`;
                    },
                },
            },
        },
        scales: {
            x: {title: {display: true, text: 'Cluster'}},
            y: {
                title: {display: true, text: 'Reports'},
                beginAtZero: true,
                position: 'left' as const,
                ticks: {precision: 0},
            },
            y1: {
                title: {display: true, text: 'Cumulative %'},
                beginAtZero: true,
                max: 100,
                position: 'right' as const,
                grid: {drawOnChartArea: false},
                ticks: {callback: (value: number | string): string => `${value}%`},
            },
        },
    }), [paretoClusters]);

    // Challenged-vs-failed scatter groups — computed off-thread by the worker.
    const scatterGroups = analysis?.scatterGroups ?? EMPTY_SCATTER;

    const scatterData = useMemo(() => {
        const minR = 4;
        const maxR = 16;
        const maxCount = Math.max(1, ...scatterGroups.map((g) => g.reports.length));

        return {
            datasets: [{
                data: scatterGroups.map((g) => ({x: g.x, y: g.y})),
                backgroundColor: scatterGroups.map((g) =>
                    g.y === 0
                        ? 'rgba(46, 125, 50, 0.7)'
                        : g.dominantCluster >= 0
                            ? 'rgba(255, 152, 0, 0.7)'
                            : 'rgba(120, 120, 120, 0.6)'
                ),
                pointRadius: scatterGroups.map((g) =>
                    minR + (g.reports.length - 1) / (maxCount - 1 || 1) * (maxR - minR)
                ),
                pointHoverRadius: scatterGroups.map((g) =>
                    minR + 2 + (g.reports.length - 1) / (maxCount - 1 || 1) * (maxR - minR)
                ),
            }],
        };
    }, [scatterGroups]);

    const scatterOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {display: false},
            tooltip: {
                callbacks: {
                    title: (items: { dataIndex: number }[]): string | string[] => {
                        const g = scatterGroups[items[0]?.dataIndex];
                        if (!g) return '';
                        const lines: string[] = [];
                        lines.push(`${g.x} challenged, ${g.y} failed — ${g.reports.length} report${g.reports.length !== 1 ? 's' : ''}`);
                        g.reports.forEach((r) => {
                            const cluster = r.clusterIdx >= 0 ? ` (C${r.clusterIdx + 1})` : ' (no failures)';
                            lines.push(`  ${r.uniqueId}${cluster}`);
                        });
                        return lines;
                    },
                    label: (): string => '',
                },
            },
        },
        interaction: {mode: 'nearest' as const, intersect: true},
        scales: {
            x: {title: {display: true, text: 'Barriers Challenged'}, beginAtZero: true, ticks: {precision: 0}},
            y: {title: {display: true, text: 'Barriers Failed'}, beginAtZero: true, ticks: {precision: 0}},
        },
    }), [scatterGroups]);

    // Co-failure heatmap data — computed off-thread by the worker.
    const heatmapData = analysis?.heatmapData ?? EMPTY_HEATMAP;

    // Executive summary metrics
    const clusterSummary = useMemo(() => {
        const total = clusters.length;
        const singletonCount = clusters.filter((c) => c.count === 1).length;
        const singletonRatio = total > 0 ? singletonCount / total : 0;

        // Top N coverage: how many clusters to reach 80%
        let cumReports = 0;
        let clustersFor80 = 0;
        for (let i = 0; i < clusters.length; i++) {
            cumReports += clusters[i].count;
            if (totalReportsInClusters > 0 && cumReports / totalReportsInClusters >= 0.8) {
                clustersFor80 = i + 1;
                break;
            }
        }
        if (clustersFor80 === 0 && total > 0) clustersFor80 = total;

        // Trend counts. Basis depends on the trend window: with a recent/prior split we
        // use the count-ratio (clusterTrendStats); in All-Time there is no split, so the
        // ratio is null for every cluster — fall back to the distribution-free Laplace
        // test over each cluster's whole history (the same all-time verdict the "Lifetime
        // trend" coloring uses). Without this, All-Time (the default) shows 0/0/0.
        const useLaplace = trendWindow == null;
        // Observation window for Laplace: the IRP date filter when set, else the full span
        // of report dates across all clusters.
        let originMs = primaryFilter.dateFrom?.getTime() ?? Infinity;
        let endMs = primaryFilter.dateTo?.getTime() ?? -Infinity;
        if (useLaplace && (primaryFilter.dateFrom == null || primaryFilter.dateTo == null)) {
            for (const c of clusters) {
                for (const r of c.reports) {
                    if (!r.dateTime) continue;
                    const t = r.dateTime.getTime();
                    if (isNaN(t)) continue;
                    if (primaryFilter.dateFrom == null && t < originMs) originMs = t;
                    if (primaryFilter.dateTo == null && t > endMs) endMs = t;
                }
            }
        }
        let risingCount = 0;
        let fallingCount = 0;
        let stableCount = 0;
        let activeCount = 0; // window mode: clusters with >=1 report in the band (matches the "Active Clusters" KPI)
        let largestRising: OccurrenceCluster | null = null as OccurrenceCluster | null;
        let largestRisingIndex = -1;
        clusters.forEach((c, idx) => {
            let trend: 'rising' | 'falling' | 'stable' | null;
            if (useLaplace) {
                const events = c.reports
                    .filter((r) => r.dateTime != null && !isNaN(r.dateTime.getTime()))
                    .map((r) => r.dateTime!.getTime());
                const verdict = laplaceTrend(events, originMs, endMs).verdict;
                trend = verdict === 'deteriorating' ? 'rising' : verdict === 'improving' ? 'falling' : verdict;
            } else {
                // Active = at least one report inside the trend band [start, end] — same
                // population as the "Active Clusters" KPI, so the denominator matches.
                if (trendWindow && c.reports.some((r) => {
                    if (!r.dateTime) return false;
                    const t = r.dateTime.getTime();
                    return !isNaN(t) && t >= trendWindow.start && t <= trendWindow.end;
                })) activeCount++;
                trend = clusterTrendStats.get(c.signatureKey)?.trend ?? null;
            }
            if (trend === 'rising') {
                risingCount++;
                if (!largestRising || c.count > largestRising.count) { largestRising = c; largestRisingIndex = idx; }
            } else if (trend === 'falling') {
                fallingCount++;
            } else if (trend === 'stable') {
                stableCount++;
            }
        });
        const trendBasis: 'window' | 'laplace' = useLaplace ? 'laplace' : 'window';
        // Denominator matches the mode's cluster KPI so the chips reconcile (↑+→+↓+? = denom):
        // window mode → active clusters ("Active Clusters" KPI); All-Time → all clusters
        // ("Clusters" KPI). The unclassified "?" chip absorbs the rest in both modes
        // (clusters with too few dated reports to classify).
        const trendDenom = useLaplace ? total : activeCount;

        // Most correlated pair (highest independence score)
        let topPair: {barrierA: string; barrierB: string; score: number} | null = null;
        const barriers = heatmapData.barriers;
        const matrix = heatmapData.matrix;
        const n = barriers.length;
        for (let i = 0; i < n; i++) {
            const totalA = matrix[i][i];
            if (totalA === 0) continue;
            for (let j = i + 1; j < n; j++) {
                const totalB = matrix[j][j];
                if (totalB === 0) continue;
                const coCount = matrix[i][j];
                if (coCount === 0) continue;
                const pBgivenA = coCount / totalA;
                const pB = mappedReports.length > 0 ? totalB / mappedReports.length : 0;
                if (pB === 0) continue;
                const score = pBgivenA / pB;
                if (!topPair || score > topPair.score) {
                    topPair = {
                        barrierA: barriers[i].uniqueId || barriers[i].name || '',
                        barrierB: barriers[j].uniqueId || barriers[j].name || '',
                        score,
                    };
                }
            }
        }

        return {
            total,
            singletonCount,
            singletonRatio,
            clustersFor80,
            risingCount,
            fallingCount,
            stableCount,
            trendBasis,
            trendDenom,
            largestRising,
            largestRisingIndex,
            topPair,
        };
    }, [clusters, totalReportsInClusters, clusterTrendStats, heatmapData, mappedReports.length, trendWindow, primaryFilter.dateFrom, primaryFilter.dateTo]);

    // Per-cluster count of reports whose dateTime sits inside the trend-window band
    // [start, end]. Empty (and irrelevant) in allTime mode. Drives the window-aware
    // Clusters / Pareto / Singleton cards below.
    const clusterInWindowCounts = useMemo((): Map<string, number> => {
        const counts = new Map<string, number>();
        if (!trendWindow) return counts;
        const {start, end} = trendWindow;
        clusters.forEach((c) => {
            let n = 0;
            c.reports.forEach((r) => {
                if (!r.dateTime) return;
                const t = r.dateTime.getTime();
                if (!isNaN(t) && t >= start && t <= end) n++;
            });
            counts.set(c.signatureKey, n);
        });
        return counts;
    }, [clusters, trendWindow]);

    // Display-ready summary metrics that switch between cumulative (all-time) and
    // band-restricted (window mode) values. The Trend Summary card reads from
    // clusterTrendStats which is already window-aware — no need to touch it here.
    const summaryDisplay = useMemo(() => {
        const inWindowMode = trendWindowMonths !== 'allTime' && trendWindow != null;
        if (!inWindowMode) {
            return {
                inWindowMode: false,
                total: clusterSummary.total,
                totalReports: totalReportsInClusters,
                singletonCount: clusterSummary.singletonCount,
                singletonRatio: clusterSummary.singletonRatio,
                clustersFor80: clusterSummary.clustersFor80,
            };
        }
        // Window mode: only count clusters with in-window activity, and use the
        // in-window report counts for Pareto + singleton logic.
        const active = clusters.filter((c) => (clusterInWindowCounts.get(c.signatureKey) ?? 0) > 0);
        const total = active.length;
        const totalReports = active.reduce(
            (s, c) => s + (clusterInWindowCounts.get(c.signatureKey) ?? 0),
            0,
        );
        const singletonCount = active.filter((c) => clusterInWindowCounts.get(c.signatureKey) === 1).length;
        const singletonRatio = total > 0 ? singletonCount / total : 0;
        // 80%-Pareto over in-window counts, descending.
        const sorted = [...active].sort(
            (a, b) => (clusterInWindowCounts.get(b.signatureKey) ?? 0) - (clusterInWindowCounts.get(a.signatureKey) ?? 0),
        );
        let cum = 0;
        let clustersFor80 = 0;
        for (let i = 0; i < sorted.length; i++) {
            cum += clusterInWindowCounts.get(sorted[i].signatureKey) ?? 0;
            if (totalReports > 0 && cum / totalReports >= 0.8) { clustersFor80 = i + 1; break; }
        }
        if (clustersFor80 === 0 && total > 0) clustersFor80 = total;
        return {inWindowMode: true, total, totalReports, singletonCount, singletonRatio, clustersFor80};
    }, [trendWindowMonths, trendWindow, clusters, clusterInWindowCounts, clusterSummary, totalReportsInClusters]);

    const SOURCE_COLORS: Record<string, string> = {
        NTSB: '#d32f2f',
        ASRS: '#f57c00',
        FAA_AIDS: '#1976d2',
        INTERNAL: '#78909c',
    };
    const FALLBACK_PALETTE = ['#7b1fa2', '#00897b', '#c2185b', '#455a64', '#5d4037'];

    const sourceClusters = useMemo(
        (): OccurrenceCluster[] => clusters.slice(0, sourceLimit),
        [clusters, sourceLimit],
    );

    const sourceData = useMemo(() => {
        const allSources = new Set<string>();
        const perCluster: Map<string, number>[] = sourceClusters.map((cluster) => {
            const counts = new Map<string, number>();
            cluster.reports.forEach((r) => {
                const src = r.sourceName || 'Unknown';
                allSources.add(src);
                counts.set(src, (counts.get(src) ?? 0) + 1);
            });
            return counts;
        });

        const sources = Array.from(allSources).sort();
        let fallbackIdx = 0;

        return {
            labels: sourceClusters.map((_, i) => `C${i + 1}`),
            datasets: sources.map((src) => ({
                label: src,
                data: perCluster.map((counts) => counts.get(src) ?? 0),
                backgroundColor: SOURCE_COLORS[src] ?? FALLBACK_PALETTE[fallbackIdx++ % FALLBACK_PALETTE.length],
            })),
        };
    }, [sourceClusters]);

    const sourceOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {display: true, position: 'top' as const},
            tooltip: {
                callbacks: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    label: (item: any): string => {
                        const source = item.dataset.label ?? '';
                        const count: number = item.parsed.y;
                        const idx = item.dataIndex;
                        const total = sourceClusters[idx]?.count ?? 1;
                        const pct = Math.round((count / total) * 100);
                        return `${source}: ${count} (${pct}%)`;
                    },
                },
            },
        },
        scales: {
            x: {stacked: true, title: {display: true, text: 'Cluster'}},
            y: {stacked: true, title: {display: true, text: 'Reports'}, beginAtZero: true, ticks: {precision: 0}},
        },
    }), [sourceClusters]);

    const [heatmapMode, setHeatmapMode] = useState<'count' | 'probability' | 'independence'>('independence');
    const heatmapTooltipRef = useRef<HTMLDivElement>(null);
    const heatmapContainerRef = useRef<HTMLDivElement>(null);

    // Build heatmap HTML string to avoid N×N React elements
    const heatmapHtml = useMemo((): string => {
        const barriers = heatmapData.barriers;
        const n = barriers.length;
        if (n === 0) return '';

        const CELL_W = 36;
        const CELL_H = 28;
        const LABEL_W = 80;

        // Use a <table> so column headers and data cells share exact column widths
        const headerCells = [`<th style="width:${LABEL_W}px;min-width:${LABEL_W}px;padding:0"></th>`];
        for (const b of barriers) {
            const label = b.uniqueId || b.name;
            headerCells.push(
                `<th style="width:${CELL_W}px;min-width:${CELL_W}px;max-width:${CELL_W}px;height:120px;padding:0;vertical-align:bottom">` +
                `<div style="transform:rotate(-60deg);transform-origin:bottom left;white-space:nowrap;font-size:0.6rem;color:#333;width:${CELL_W}px;margin-left:${CELL_W / 2 + 4}px;margin-bottom:4px" title="${label.replace(/"/g, '&quot;')}">${label}</div>` +
                '</th>'
            );
        }

        // Data rows
        const bodyRows: string[] = [];
        for (let ri = 0; ri < n; ri++) {
            const rowBarrier = barriers[ri];
            const rowLabel = rowBarrier.uniqueId || rowBarrier.name;
            const rowTotal = heatmapData.matrix[ri][ri];
            const cells = [
                `<td style="width:${LABEL_W}px;min-width:${LABEL_W}px;max-width:${LABEL_W}px;font-size:0.6rem;text-align:right;padding-right:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${rowLabel.replace(/"/g, '&quot;')}">${rowLabel}</td>`
            ];
            for (let ci = 0; ci < n; ci++) {
                const count = heatmapData.matrix[ri][ci];
                const isDiag = ri === ci;
                const colBarrier = barriers[ci];
                const colTotal = heatmapData.matrix[ci][ci];
                const prob = rowTotal > 0 ? count / rowTotal : 0;
                const pB = mappedReports.length > 0 ? colTotal / mappedReports.length : 0;
                const indRatio = (pB > 0 && rowTotal > 0) ? prob / pB : null;

                let bgColor: string;
                let cellText: string;
                let intensity: number;

                if (heatmapMode === 'independence') {
                    if (isDiag) {
                        bgColor = '#e0e0e0'; cellText = '—'; intensity = 0;
                    } else if (count === 0 || indRatio == null) {
                        bgColor = '#fafafa'; cellText = ''; intensity = 0;
                    } else {
                        const ratioStr = indRatio >= 10 ? Math.round(indRatio).toFixed(0) : indRatio.toFixed(1);
                        cellText = `${ratioStr}×`;
                        if (indRatio >= 0.9 && indRatio <= 1.1) {
                            const greenIntensity = 1 - Math.abs(indRatio - 1.0) / 0.1;
                            bgColor = `rgba(46,125,50,${(0.1 + 0.3 * greenIntensity).toFixed(2)})`;
                            intensity = 0;
                        } else if (indRatio < 0.9) {
                            const blueIntensity = Math.min((0.9 - indRatio) / 0.9, 1);
                            bgColor = `rgba(21,101,192,${(0.15 + 0.55 * blueIntensity).toFixed(2)})`;
                            intensity = blueIntensity;
                        } else {
                            const ri2 = Math.min((indRatio - 1.1) / 4, 1);
                            bgColor = `rgba(211,47,47,${(0.15 + ri2 * 0.85).toFixed(2)})`;
                            intensity = ri2;
                        }
                    }
                } else if (heatmapMode === 'probability') {
                    if (isDiag) {
                        const selfProb = mappedReports.length > 0 ? count / mappedReports.length : 0;
                        bgColor = '#e0e0e0'; cellText = count > 0 ? `${Math.round(selfProb * 100)}%` : ''; intensity = 0;
                    } else {
                        intensity = prob;
                        cellText = count > 0 ? `${Math.round(prob * 100)}%` : '';
                        bgColor = count === 0 ? '#fafafa' : `rgba(211,47,47,${(0.15 + intensity * 0.85).toFixed(2)})`;
                    }
                } else {
                    if (isDiag) {
                        bgColor = '#e0e0e0'; cellText = count > 0 ? String(count) : ''; intensity = 0;
                    } else {
                        intensity = count / heatmapData.maxCount;
                        cellText = count > 0 ? String(count) : '';
                        bgColor = count === 0 ? '#fafafa' : `rgba(211,47,47,${(0.15 + intensity * 0.85).toFixed(2)})`;
                    }
                }

                const diagNeutral = isDiag;
                const fgColor = diagNeutral ? '#666' : intensity > 0.5 ? '#fff' : '#333';
                const fw = isDiag ? 700 : 400;

                cells.push(
                    `<td data-row="${ri}" data-col="${ci}" data-count="${count}" data-row-id="${(rowBarrier.uniqueId || rowBarrier.name).replace(/"/g, '&quot;')}" data-col-id="${(colBarrier.uniqueId || colBarrier.name).replace(/"/g, '&quot;')}" data-row-total="${rowTotal}" data-col-total="${colTotal}" data-diag="${isDiag ? 1 : 0}" style="width:${CELL_W}px;min-width:${CELL_W}px;max-width:${CELL_W}px;height:${CELL_H}px;text-align:center;background:${bgColor};border:1px solid #e0e0e0;cursor:default;padding:0">` +
                    `<span style="font-size:0.6rem;font-weight:${fw};color:${fgColor}">${cellText}</span></td>`
                );
            }
            bodyRows.push(`<tr>${cells.join('')}</tr>`);
        }

        return `<table style="border-collapse:collapse"><thead><tr>${headerCells.join('')}</tr></thead><tbody>${bodyRows.join('')}</tbody></table>`;
    }, [heatmapData, heatmapMode, mappedReports.length]);

    const handleHeatmapMouseMove = useCallback((e: React.MouseEvent): void => {
        const tooltip = heatmapTooltipRef.current;
        const container = heatmapContainerRef.current;
        if (!tooltip || !container) return;

        const target = e.target as HTMLElement;
        const cell = target.closest('[data-row][data-col]') as HTMLElement | null;
        if (!cell) {
            tooltip.style.display = 'none';
            return;
        }

        const rowId = cell.dataset.rowId ?? '';
        const colId = cell.dataset.colId ?? '';
        const count = parseInt(cell.dataset.count ?? '0', 10);
        const rowTotal = parseInt(cell.dataset.rowTotal ?? '0', 10);
        const colTotal = parseInt(cell.dataset.colTotal ?? '0', 10);
        const isDiag = cell.dataset.diag === '1';
        const prob = rowTotal > 0 ? count / rowTotal : 0;
        const pB = mappedReports.length > 0 ? colTotal / mappedReports.length : 0;
        const indRatio = (pB > 0 && rowTotal > 0) ? prob / pB : null;

        let text: string;
        if (heatmapMode === 'independence') {
            if (isDiag) {
                text = `${colId} (self)`;
            } else if (count === 0 || indRatio == null) {
                text = `${rowId} & ${colId}: no co-failures`;
            } else {
                const ratioStr = indRatio >= 10 ? Math.round(indRatio).toFixed(0) : indRatio.toFixed(1);
                const desc = indRatio < 0.9 ? 'below expected' : indRatio <= 1.1 ? 'independent' : indRatio <= 2 ? 'weakly correlated' : 'strongly correlated';
                text = `P(${colId}|${rowId}) / P(${colId}) = ${ratioStr}× — ${desc}`;
            }
        } else if (heatmapMode === 'probability') {
            if (isDiag) {
                const selfProb = mappedReports.length > 0 ? count / mappedReports.length : 0;
                text = `P(${colId} fails) = ${count}/${mappedReports.length} = ${Math.round(selfProb * 100)}%`;
            } else {
                text = `P(${colId} fails | ${rowId} fails) = ${count}/${rowTotal} = ${Math.round(prob * 100)}%`;
            }
        } else {
            text = `${rowId} & ${colId}: ${count} co-failure${count !== 1 ? 's' : ''}${isDiag ? ' (self)' : ''}`;
        }

        tooltip.textContent = text;
        const containerRect = container.getBoundingClientRect();
        tooltip.style.display = 'block';
        tooltip.style.left = `${e.clientX - containerRect.left + container.scrollLeft + 12}px`;
        tooltip.style.top = `${e.clientY - containerRect.top + container.scrollTop - 28}px`;
    }, [heatmapMode, mappedReports.length]);

    const handleHeatmapMouseLeave = useCallback((): void => {
        if (heatmapTooltipRef.current) heatmapTooltipRef.current.style.display = 'none';
    }, []);

    if (!barrierAnalysisCache && !dataSource) return null;
    if (!barrierAnalysisCache || !analysis) {
        return (
            <Box sx={{p: 4, textAlign: 'center'}}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    {!barrierAnalysisCache ? 'Loading barrier data...' : 'Building report clusters...'}
                </Typography>
                <LinearProgress sx={{maxWidth: 300, mx: 'auto'}} />
            </Box>
        );
    }

    // Guard: stale data from different IRP
    if (irpDetails && irpDetails.id !== irpId) {
        return null;
    }

    // Empty state: no barrier nodes
    if (barrierNodes.length === 0) {
        return (
            <Box sx={{p: 3, textAlign: 'center'}}>
                <Typography variant="body1" color="text.secondary">
                    No barrier nodes found in the fault tree.
                </Typography>
            </Box>
        );
    }

    // Empty state: no mapped reports
    if (mappedReports.length === 0) {
        return (
            <Box sx={{p: 3, textAlign: 'center'}}>
                <Typography variant="body1" color="text.secondary">
                    No mapped reports found. Reports must be processed and mapped before occurrence patterns can be analyzed.
                </Typography>
            </Box>
        );
    }

    // Empty state: no clusters (all mapped reports have no barrier data)
    if (clusters.length === 0) {
        return (
            <Box sx={{p: 3, textAlign: 'center'}}>
                <Typography variant="body1" color="text.secondary">
                    No barrier outcome data found for the mapped reports.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto'}}>
            {/* Objective description */}
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    flexShrink: 0,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&::before': {display: 'none'},
                    mb: 1,
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{minHeight: 40, '& .MuiAccordionSummary-content': {my: 0.5}}}>
                    <Typography variant="body2" color="text.secondary">
                        Report Clustering groups incidents by identical barrier failure signatures to surface recurring patterns. Click to learn why it matters.
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{pt: 0, pb: 1}}>
                    <List dense disablePadding>
                        <ListItem>
                            <ListItemText
                                primary="Identify barrier dependencies"
                                secondary="Barriers that repeatedly co-fail may share a common dependency (same crew, sensor, or procedure) — pointing to a systemic weakness that needs investigation."
                                slotProps={{primary: {variant: 'subtitle2', fontSize: '0.8rem'}, secondary: {variant: 'caption'}}}
                            />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                            <ListItemText
                                primary="Distinguish systemic from isolated failures"
                                secondary="A barrier may be broadly weak or failing only with specific partners — these require different mitigations."
                                slotProps={{primary: {variant: 'subtitle2', fontSize: '0.8rem'}, secondary: {variant: 'caption'}}}
                            />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                            <ListItemText
                                primary="Spot emerging patterns"
                                secondary="The temporal trend (rising/falling/stable) reveals whether a failure pattern is getting worse over time. A rising cluster might indicate a degrading system or a new operational risk."
                                slotProps={{primary: {variant: 'subtitle2', fontSize: '0.8rem'}, secondary: {variant: 'caption'}}}
                            />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                            <ListItemText
                                primary="Prioritize mitigations"
                                secondary="A cluster with 200 reports is more urgent than one with 3."
                                slotProps={{primary: {variant: 'subtitle2', fontSize: '0.8rem'}, secondary: {variant: 'caption'}}}
                            />
                        </ListItem>
                    </List>
                </AccordionDetails>
            </Accordion>
            {/* Sticky toolbar wrapper — keeps the sub-tab nav and chart-type bar visible while scrolling */}
            <Box sx={{
                position: 'sticky', top: 0, zIndex: 3,
                backgroundColor: 'background.paper',
                pt: 0.5,
            }}>
                {/* Sub-tab nav — mutually-exclusive Summary / Charts / Clusters, mirroring the
                Barrier Effectiveness restructure. Each sub-tab owns its own controls below. */}
                <Box sx={{display: 'flex', alignItems: 'center', mb: 1, gap: 1}}>
                    <Tabs
                        value={activeSubTab}
                        onChange={(_e, v): void => setActiveSubTab(v as ClusterSubTab)}
                        variant="standard"
                        sx={{minHeight: 36, '& .MuiTab-root': {minHeight: 36, fontSize: '0.75rem', textTransform: 'none', py: 0}}}
                    >
                        <Tab value="summary" icon={<DashboardIcon fontSize="small" />} iconPosition="start" label="Dashboard" />
                        <Tab value="clusters" icon={<ViewListIcon fontSize="small" />} iconPosition="start" label="Clusters" />
                        <Tab value="charts" icon={<AnalyticsIcon fontSize="small" />} iconPosition="start" label="Charts" />
                    </Tabs>
                    <Box sx={{flexGrow: 1}} />
                    {/* Trend window — global to Report Clustering: drives the Summary cards,
                    the temporal chart coloring/band, and the ↑↓→ arrows on every cluster
                    row. Lives in the global header so its selection is visible and editable
                    from every sub-tab, not duplicated per sub-tab. */}
                    <TrendWindowSelect
                        value={trendWindowMonths}
                        onChange={setTrendWindowMonths}
                        tooltip="Trend window — splits the date range into recent vs. prior halves. Drives the Summary's active-cluster count / 80% Pareto coverage / singleton ratio, the temporal chart coloring, and the ↑/→/↓ trend arrows on each cluster row. Mid-point splits the IRP range at its midpoint; numeric values use the last N months; All time disables the split."
                    />
                </Box>

                {/* Chart-type selector — only on the Charts sub-tab. Picks which distribution
                chart is shown; the panel itself always renders on this sub-tab. */}
                {activeSubTab === 'charts' && (
                    <Box sx={{display: 'flex', alignItems: 'center', mb: 1, gap: 0.5}}>
                        <Tooltip title="Pareto chart">
                            <IconButton
                                size="small"
                                onClick={(): void => setChartMode('pareto')}
                                color={chartMode === 'pareto' ? 'primary' : 'default'}
                            >
                                <BarChartIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Challenged vs. Failed per report">
                            <IconButton
                                size="small"
                                onClick={(): void => setChartMode('scatter')}
                                color={chartMode === 'scatter' ? 'primary' : 'default'}
                            >
                                <BubbleChartIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Temporal distribution">
                            <IconButton
                                size="small"
                                onClick={(): void => setChartMode('temporal')}
                                color={chartMode === 'temporal' ? 'primary' : 'default'}
                            >
                                <TimelineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Co-failure heatmap">
                            <IconButton
                                size="small"
                                onClick={(): void => setChartMode('heatmap')}
                                color={chartMode === 'heatmap' ? 'primary' : 'default'}
                            >
                                <GridOnIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Source distribution">
                            <IconButton
                                size="small"
                                onClick={(): void => setChartMode('source')}
                                color={chartMode === 'source' ? 'primary' : 'default'}
                            >
                                <StackedBarChartIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {/* Executive Summary */}
            {activeSubTab === 'summary' && (
                <Paper
                    variant="outlined"
                    sx={{
                        mb: 1.5, px: 2, py: 1.5, display: 'flex', flexDirection: 'column',
                        ...(chartFullscreen && {
                            ...FULLSCREEN_OVERLAY_SX,
                            overflow: 'auto',
                        }),
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', mb: 1.5, gap: 1}}>
                        <Box sx={{flexGrow: 1}} />
                        <FullscreenToggle active={chartFullscreen} onToggle={(): void => setChartFullscreen((f) => !f)} />
                    </Box>
                    <Stack direction="row" spacing={2} sx={{mb: 1}} flexWrap="wrap" useFlexGap>
                        {/* Overview */}
                        <KpiCard
                            label={summaryDisplay.inWindowMode ? 'Active Clusters' : 'Clusters'}
                            value={summaryDisplay.total}
                            subtext={`${summaryDisplay.totalReports} report${summaryDisplay.totalReports !== 1 ? 's' : ''}${summaryDisplay.inWindowMode ? ' in window' : `, ${clusterBarrierNodes.length} barrier${clusterBarrierNodes.length !== 1 ? 's' : ''}`}`}
                        />

                        {/* Pattern Concentration */}
                        <KpiCard
                            tooltip={summaryDisplay.inWindowMode
                                ? `${summaryDisplay.clustersFor80} of ${summaryDisplay.total} active clusters account for 80% of in-window reports`
                                : `${summaryDisplay.clustersFor80} of ${summaryDisplay.total} clusters account for 80% of all clustered reports`}
                            label="80% Pareto Coverage"
                            value={summaryDisplay.clustersFor80}
                            subtext={`of ${summaryDisplay.total} clusters`}
                        />

                        {/* Singleton Ratio */}
                        <KpiCard
                            tooltip={summaryDisplay.inWindowMode
                                ? `${summaryDisplay.singletonCount} clusters had exactly 1 in-window report — unique, non-repeating failure patterns inside the trend window`
                                : `${summaryDisplay.singletonCount} clusters have only 1 report — unique, non-repeating failure patterns`}
                            label="One-off Patterns"
                            value={`${Math.round(summaryDisplay.singletonRatio * 100)}%`}
                            subtext={`${summaryDisplay.singletonCount} singleton cluster${summaryDisplay.singletonCount !== 1 ? 's' : ''}`}
                        />

                        {/* Trend Summary */}
                        {((): React.ReactNode => {
                            // Name the basis so the chips aren't misread: All-Time uses the
                            // distribution-free Laplace verdict over each cluster's full history;
                            // a numeric/mid-point window uses the recent-vs-prior count ratio.
                            const isLaplace = clusterSummary.trendBasis === 'laplace';
                            const basis = isLaplace ? 'all-time (Laplace trend test)' : 'recent vs prior window';
                            // Active clusters that couldn't be classified (too few dated reports
                            // to compare). Makes ↑ + → + ↓ + ? reconcile with the denominator.
                            const unclassified = clusterSummary.trendDenom - clusterSummary.risingCount - clusterSummary.stableCount - clusterSummary.fallingCount;
                            return (
                                <Paper elevation={0} sx={{px: 2, py: 1.5, minWidth: 140, textAlign: 'center', border: '1px solid', borderColor: 'divider'}}>
                                    <Typography variant="caption" color="text.secondary">Trend Summary</Typography>
                                    <Stack direction="row" spacing={1} justifyContent="center" sx={{mt: 0.5}}>
                                        <Tooltip title={`${clusterSummary.risingCount} cluster${clusterSummary.risingCount !== 1 ? 's' : ''} deteriorating — getting worse · basis: ${basis}`} arrow>
                                            <Chip label={`${clusterSummary.risingCount} ↑`} size="small" sx={{backgroundColor: 'rgba(211, 47, 47, 0.15)', color: '#d32f2f', fontWeight: 'bold', cursor: 'help'}} />
                                        </Tooltip>
                                        <Tooltip title={`${clusterSummary.stableCount} cluster${clusterSummary.stableCount !== 1 ? 's' : ''} stable · basis: ${basis}`} arrow>
                                            <Chip label={`${clusterSummary.stableCount} →`} size="small" sx={{backgroundColor: 'rgba(120, 120, 120, 0.15)', color: '#666', fontWeight: 'bold', cursor: 'help'}} />
                                        </Tooltip>
                                        <Tooltip title={`${clusterSummary.fallingCount} cluster${clusterSummary.fallingCount !== 1 ? 's' : ''} improving · basis: ${basis}`} arrow>
                                            <Chip label={`${clusterSummary.fallingCount} ↓`} size="small" sx={{backgroundColor: 'rgba(46, 125, 50, 0.15)', color: '#2e7d32', fontWeight: 'bold', cursor: 'help'}} />
                                        </Tooltip>
                                        {unclassified > 0 && (
                                            <Tooltip title={`${unclassified} cluster${unclassified !== 1 ? 's' : ''} with too few reports to classify (need ≥ ${isLaplace ? 4 : 2})`} arrow>
                                                <Chip label={`${unclassified} ?`} size="small" variant="outlined" sx={{backgroundColor: 'transparent', color: '#9e9e9e', border: '1px dashed #bdbdbd', fontWeight: 'bold', cursor: 'help'}} />
                                            </Tooltip>
                                        )}
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
                                        of {clusterSummary.trendDenom} {isLaplace ? '' : 'active '}cluster{clusterSummary.trendDenom !== 1 ? 's' : ''}
                                    </Typography>
                                </Paper>
                            );
                        })()}

                        {/* Most Correlated Pair */}
                        {clusterSummary.topPair && (
                            <KpiCard
                                tooltip={`Independence score: ${clusterSummary.topPair.score.toFixed(1)}× — these barriers co-fail ${clusterSummary.topPair.score.toFixed(1)} times more often than expected`}
                                label="Most Correlated Pair"
                                value={`${clusterSummary.topPair.score.toFixed(1)}×`}
                                valueColor={clusterSummary.topPair.score > 2 ? '#d32f2f' : '#f57c00'}
                                backgroundColor={clusterSummary.topPair.score > 2 ? 'rgba(211, 47, 47, 0.08)' : 'rgba(255, 152, 0, 0.08)'}
                                borderColor={clusterSummary.topPair.score > 2 ? 'rgba(211, 47, 47, 0.3)' : 'rgba(255, 152, 0, 0.3)'}
                                subtextSx={{lineHeight: 1.2}}
                                subtext={<>{clusterSummary.topPair.barrierA}<br />{clusterSummary.topPair.barrierB}</>}
                            />
                        )}

                        {/* New Clusters — clickable KPI. Jumps to the Clusters sub-tab
                            with the "New only" filter applied so the analyst can drill
                            into the emerging patterns. */}
                        <KpiCard
                            tooltip={newClusters.length > 0
                                ? `${newClusters.length} cluster${newClusters.length !== 1 ? 's' : ''} whose reports all fall within the last ${newClusterMonths} month${newClusterMonths !== 1 ? 's' : ''} — newly emerging patterns. Click to view them in the Clusters tab.`
                                : `No clusters are made up exclusively of reports from the last ${newClusterMonths} month${newClusterMonths !== 1 ? 's' : ''}.`}
                            onClick={(): void => { setNewOnly(true); setClusterPage(0); setActiveSubTab('clusters'); }}
                            label="New Clusters"
                            value={newClusters.length}
                            valueColor={newClusters.length > 0 ? '#0288d1' : 'text.primary'}
                            backgroundColor={newClusters.length > 0 ? 'rgba(2, 136, 209, 0.08)' : undefined}
                            borderColor={newClusters.length > 0 ? 'rgba(2, 136, 209, 0.3)' : 'divider'}
                            subtext={`last ${newClusterMonths} mo · view →`}
                        />
                    </Stack>

                    {/* Largest rising cluster callout */}
                    {clusterSummary.largestRising && (
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5}}>
                            Largest rising cluster: C{clusterSummary.largestRisingIndex + 1} — {clusterSummary.largestRising.barriers.map((b) => b.uniqueId || b.name).join(', ')} ({clusterSummary.largestRising.count} reports)
                        </Typography>
                    )}

                </Paper>
            )}

            {/* Charts sub-tab: distribution chart */}
            {activeSubTab === 'charts' && (
                <Paper
                    ref={chartPanelRef}
                    variant="outlined"
                    sx={{
                        mb: 2, display: 'flex', flexDirection: 'column', position: 'relative', scrollMarginTop: '84px',
                        ...(chartFullscreen && FULLSCREEN_OVERLAY_SX),
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 1, pt: 0.5}}>
                        <FullscreenToggle active={chartFullscreen} onToggle={(): void => setChartFullscreen((f) => !f)} />
                    </Box>
                    <Box sx={{height: chartFullscreen ? '100%' : chartHeight, minHeight: 100, flex: chartFullscreen ? 1 : undefined, p: 1.5, pt: 0, overflow: 'hidden'}}>
                        {chartMode === 'pareto' && (
                            <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <Typography variant="caption" sx={{color: 'text.secondary', mb: 0.5, flexShrink: 0}}>
                                    Clusters ranked by frequency — the 80% line shows how many failure patterns account for most incidents.
                                </Typography>
                                {clusters.length > 50 && (
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.5, flexShrink: 0}}>
                                        <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>
                                            Showing top {paretoLimit >= clusters.length ? 'all' : paretoLimit} of {clusters.length} clusters
                                        </Typography>
                                        <FormControl size="small" sx={{minWidth: 70}}>
                                            <Select
                                                value={paretoLimit}
                                                onChange={(e): void => setParetoLimit(e.target.value as number)}
                                                sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                                            >
                                                {[50, 100, 200, 500].filter((v) => v < clusters.length).map((v) => (
                                                    <MenuItem key={v} value={v} sx={{fontSize: '0.7rem'}}>{v}</MenuItem>
                                                ))}
                                                <MenuItem value={clusters.length} sx={{fontSize: '0.7rem'}}>All</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                )}
                                <Box sx={{flexGrow: 1, minHeight: 0, overflow: 'hidden'}}>
                                    <Chart type="bar" data={paretoData} options={paretoOptions} plugins={[thresholdLinesPlugin]} />
                                </Box>
                            </Box>
                        )}
                        {chartMode === 'scatter' && (
                            <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <Typography variant="caption" sx={{color: 'text.secondary', mb: 0.5, flexShrink: 0}}>
                                    Each dot is a position where one or more reports share the same challenged/failed counts. Dot size = number of reports. Points on the diagonal (y=x) had every challenged barrier fail. Color = cluster when all reports share one cluster, gray when mixed.
                                </Typography>
                                <Box sx={{flexGrow: 1, minHeight: 0, overflow: 'hidden'}}>
                                    <Scatter data={scatterData} options={scatterOptions} plugins={[scatterDiagonalPlugin]} />
                                </Box>
                                <Box sx={{display: 'flex', gap: 2, mt: 0.5, flexShrink: 0, alignItems: 'center'}}>
                                    <Box sx={{display: 'flex', gap: 0.5, alignItems: 'center'}}>
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(46, 125, 50, 0.7)', flexShrink: 0}} />
                                        <Typography variant="caption" sx={{fontSize: '0.65rem', color: 'text.secondary'}}>
                                            No failures
                                        </Typography>
                                    </Box>
                                    <Box sx={{display: 'flex', gap: 0.5, alignItems: 'center'}}>
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(255, 152, 0, 0.7)', flexShrink: 0}} />
                                        <Typography variant="caption" sx={{fontSize: '0.65rem', color: 'text.secondary'}}>
                                            Single cluster
                                        </Typography>
                                    </Box>
                                    <Box sx={{display: 'flex', gap: 0.5, alignItems: 'center'}}>
                                        <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(120, 120, 120, 0.6)', flexShrink: 0}} />
                                        <Typography variant="caption" sx={{fontSize: '0.65rem', color: 'text.secondary'}}>
                                            Mixed clusters
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        {chartMode === 'temporal' && (
                            <TemporalDistributionPanel
                                entities={temporalEntities}
                                totalEntityCount={clusters.length}
                                trendWindow={trendWindow}
                                trendWindowMode={trendWindowMonths}
                                rangeFrom={primaryFilter.dateFrom}
                                rangeTo={primaryFilter.dateTo}
                                sort={temporalSort}
                                onSortChange={setTemporalSort}
                                minClusterCount={minClusterCount}
                                onMinClusterCountChange={setMinClusterCount}
                                hoveredKey={hoveredClusterKey}
                                onHoverKey={setHoveredClusterKey}
                                onEntityClick={(key): void => {
                                    // Open the time-to-failure drill-down for the clicked cluster
                                    // (mirrors BE's Failure pattern → MTBF gap dialog). The dialog's
                                    // "Show in cluster list" button performs the old list-nav.
                                    const c = clusters.find((cl) => cl.signatureKey === key);
                                    if (c) setGapDistCluster(c);
                                }}
                            />
                        )}
                        {chartMode === 'source' && (
                            <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <Typography variant="caption" sx={{color: 'text.secondary', mb: 0.5, flexShrink: 0}}>
                                    Breaks down each cluster by report source (NTSB, ASRS, etc.). A cluster dominated by one source may reflect reporting bias; clusters appearing across multiple sources are more likely systemic.
                                </Typography>
                                {clusters.length > 50 && (
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.5, flexShrink: 0}}>
                                        <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>
                                            Showing top {sourceLimit >= clusters.length ? 'all' : sourceLimit} of {clusters.length} clusters
                                        </Typography>
                                        <FormControl size="small" sx={{minWidth: 70}}>
                                            <Select
                                                value={sourceLimit}
                                                onChange={(e): void => setSourceLimit(e.target.value as number)}
                                                sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                                            >
                                                {[50, 100, 200, 500].filter((v) => v < clusters.length).map((v) => (
                                                    <MenuItem key={v} value={v} sx={{fontSize: '0.7rem'}}>{v}</MenuItem>
                                                ))}
                                                <MenuItem value={clusters.length} sx={{fontSize: '0.7rem'}}>All</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                )}
                                <Box sx={{flexGrow: 1, minHeight: 0, overflow: 'hidden'}}>
                                    <Chart type="bar" data={sourceData} options={sourceOptions} />
                                </Box>
                            </Box>
                        )}
                        {chartMode === 'heatmap' && (
                            <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <Typography variant="caption" sx={{color: 'text.secondary', mb: 0.5, flexShrink: 0}}>
                                    Shows how often pairs of barriers fail together. Dark cells indicate frequent co-failure — a sign of shared dependencies.
                                    <br />
                                    Use Independence Score mode to distinguish correlated failures from coincidence: values above 1.0x mean barriers co-fail more often than expected.
                                </Typography>
                                <Box sx={{display: 'flex', gap: 0.5, mb: 1.5, flexShrink: 0}}>
                                    <Button
                                        size="small"
                                        variant={heatmapMode === 'count' ? 'contained' : 'outlined'}
                                        onClick={(): void => setHeatmapMode('count')}
                                        sx={{fontSize: '0.65rem', py: 0, px: 1, minHeight: 22, textTransform: 'none'}}
                                    >
                                        Co-failure Count
                                    </Button>
                                    <Button
                                        size="small"
                                        variant={heatmapMode === 'probability' ? 'contained' : 'outlined'}
                                        onClick={(): void => setHeatmapMode('probability')}
                                        sx={{fontSize: '0.65rem', py: 0, px: 1, minHeight: 22, textTransform: 'none'}}
                                    >
                                        P(col fails | row fails)
                                    </Button>
                                    <Button
                                        size="small"
                                        variant={heatmapMode === 'independence' ? 'contained' : 'outlined'}
                                        onClick={(): void => setHeatmapMode('independence')}
                                        sx={{fontSize: '0.65rem', py: 0, px: 1, minHeight: 22, textTransform: 'none'}}
                                    >
                                        Independence Score
                                    </Button>
                                </Box>
                                <Box
                                    ref={heatmapContainerRef}
                                    onMouseMove={handleHeatmapMouseMove}
                                    onMouseLeave={handleHeatmapMouseLeave}
                                    sx={{flex: 1, overflow: 'auto', position: 'relative'}}
                                >
                                    {heatmapData.barriers.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{p: 2, textAlign: 'center'}}>
                                            No barrier failures to display.
                                        </Typography>
                                    ) : (
                                        <div
                                            style={{display: 'inline-block', minWidth: 'fit-content'}}
                                            dangerouslySetInnerHTML={{__html: heatmapHtml}}
                                        />
                                    )}
                                    <div
                                        ref={heatmapTooltipRef}
                                        style={{
                                            display: 'none',
                                            position: 'absolute',
                                            backgroundColor: 'rgba(0,0,0,0.8)',
                                            color: '#fff',
                                            padding: '4px 8px',
                                            borderRadius: 4,
                                            fontSize: 11,
                                            pointerEvents: 'none',
                                            whiteSpace: 'nowrap',
                                            zIndex: 10,
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    {!chartFullscreen && <div
                        onMouseDown={handleChartResizeStart}
                        onMouseEnter={(e): void => {
                            e.currentTarget.style.cursor = 'row-resize';
                            const bar = e.currentTarget.firstElementChild as HTMLElement | null;
                            if (bar) { bar.style.backgroundColor = '#1976d2'; bar.style.height = '3px'; }
                        }}
                        onMouseLeave={(e): void => {
                            const bar = e.currentTarget.firstElementChild as HTMLElement | null;
                            if (bar) { bar.style.backgroundColor = '#e0e0e0'; bar.style.height = '2px'; }
                        }}
                        style={{
                            height: 14,
                            cursor: 'row-resize',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            zIndex: 2,
                        }}
                    >
                        <div style={{width: 40, height: 2, borderRadius: 1, backgroundColor: '#e0e0e0', transition: 'all 0.15s', pointerEvents: 'none'}} />
                    </div>}
                </Paper>
            )}

            {/* Clusters sub-tab: pagination controls + cluster list */}
            {activeSubTab === 'clusters' && (<>
                {/* Cluster pagination — sticks just below the sticky sub-tab/chart header.
                Two rows: row 1 shapes the list (search, top-level, trend window, sort),
                row 2 navigates it (page size, counts, arrows). Keeping them on a single
                row crowded the labels into wraps and truncated long sort options. */}
                {clusters.length > 0 && (
                    <Box
                        ref={detailedAnalysisRef}
                        sx={{borderBottom: 1, borderColor: 'divider', mb: 1, position: 'sticky', top: '80px', zIndex: 2, backgroundColor: 'background.paper', scrollMarginTop: '84px'}}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{py: 0.5, px: 1}}>
                            {childBarrierIds.size > 0 && (
                                <Tooltip title="Exclude sub-barriers from clustering signatures — only use top-level barriers">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={topLevelOnly}
                                                onChange={(e): void => setTopLevelOnly(e.target.checked)}
                                                size="small"
                                                sx={{py: 0}}
                                            />
                                        }
                                        label={<Typography variant="caption">Top-level barriers only</Typography>}
                                        sx={{mr: 1, ml: 0}}
                                    />
                                </Tooltip>
                            )}
                            {/* New only — restrict to newly-emerging clusters (all reports within
                            the last N months). The month selector stays disabled until the
                            toggle is on so the active "new" window is always visible. */}
                            <Tooltip title="Show only newly-emerging clusters — those whose reports ALL fall within the selected recent window. Drilled into from the Dashboard's New Clusters card.">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newOnly}
                                            onChange={(e): void => { setNewOnly(e.target.checked); setClusterPage(0); }}
                                            size="small"
                                            sx={{py: 0}}
                                        />
                                    }
                                    label={<Typography variant="caption">New only</Typography>}
                                    sx={{mr: 0.5, ml: 0}}
                                />
                            </Tooltip>
                            <FormControl size="small" sx={{minWidth: 92, mr: 1}}>
                                <Select
                                    value={newClusterMonths}
                                    onChange={(e): void => { setNewClusterMonths(e.target.value as number); setClusterPage(0); }}
                                    disabled={!newOnly}
                                    sx={{fontSize: '0.75rem', height: 28, '& .MuiSelect-select': {py: 0, px: 1}}}
                                >
                                    {[1, 3, 6, 12].map((m) => (
                                        <MenuItem key={m} value={m} sx={{fontSize: '0.75rem'}}>last {m} mo</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                placeholder="Search barriers — comma/space separated, AND (e.g. B12, B07)"
                                value={clusterSearch}
                                onChange={(e): void => { setClusterSearch(e.target.value); setClusterPage(0); }}
                                sx={{flexGrow: 1, maxWidth: 600, '& .MuiInputBase-root': {fontSize: '0.75rem', height: 28, py: 0}, '& .MuiInputBase-input': {py: 0}}}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{mr: 0.5}}>
                                            <SearchIcon sx={{fontSize: 16, color: 'text.secondary'}} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: clusterSearch ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" sx={{padding: '2px'}} onClick={(): void => { setClusterSearch(''); setClusterPage(0); }}>
                                                <CloseIcon sx={{fontSize: 14}} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <Box sx={{flex: 1}} />
                            <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', ml: 1, whiteSpace: 'nowrap'}}>Sort</Typography>
                            <FormControl size="small" sx={{minWidth: 140}}>
                                <Select
                                    value={temporalSort}
                                    onChange={(e): void => { setTemporalSort(e.target.value as TemporalSort); setClusterPage(0); }}
                                    sx={{fontSize: '0.75rem', height: 28, '& .MuiSelect-select': {py: 0, px: 1}}}
                                >
                                    <MenuItem value="count" sx={{fontSize: '0.75rem'}}>By cluster size</MenuItem>
                                    <MenuItem value="rising-first" disabled={trendWindowMonths === 'allTime'} sx={{fontSize: '0.75rem'}}>Rising first</MenuItem>
                                    <MenuItem value="most-accelerating" disabled={trendWindowMonths === 'allTime'} sx={{fontSize: '0.75rem'}}>Most accelerating</MenuItem>
                                    <MenuItem value="falling-first" disabled={trendWindowMonths === 'allTime'} sx={{fontSize: '0.75rem'}}>Falling first</MenuItem>
                                </Select>
                            </FormControl>
                            <Tooltip title="Hide clusters with fewer than this many reports inside the trend window. Linked to the Window selector." arrow>
                                <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', ml: 1, whiteSpace: 'nowrap', cursor: 'help'}}>Min in window</Typography>
                            </Tooltip>
                            <FormControl size="small" sx={{minWidth: 70}}>
                                <Select
                                    value={minClusterCount}
                                    onChange={(e): void => { setMinClusterCount(Number(e.target.value)); setClusterPage(0); }}
                                    sx={{fontSize: '0.75rem', height: 28, '& .MuiSelect-select': {py: 0, px: 1}}}
                                >
                                    <MenuItem value={1} sx={{fontSize: '0.75rem'}}>All</MenuItem>
                                    <MenuItem value={2} sx={{fontSize: '0.75rem'}}>≥2</MenuItem>
                                    <MenuItem value={3} sx={{fontSize: '0.75rem'}}>≥3</MenuItem>
                                    <MenuItem value={5} sx={{fontSize: '0.75rem'}}>≥5</MenuItem>
                                    <MenuItem value={10} sx={{fontSize: '0.75rem'}}>≥10</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{py: 0.5, px: 1}}>
                            <Box sx={{flex: 1}} />
                            <Typography variant="caption" sx={{color: 'text.secondary', whiteSpace: 'nowrap'}}>
                            Per page:
                            </Typography>
                            <FormControl size="small" sx={{minWidth: 60}}>
                                <Select
                                    value={clustersPerPage}
                                    onChange={(e): void => { setClustersPerPage(e.target.value as number); setClusterPage(0); }}
                                    sx={{fontSize: '0.75rem', height: 28, '& .MuiSelect-select': {py: 0, px: 1}}}
                                >
                                    {[5, 10, 25, 50].map((v) => (
                                        <MenuItem key={v} value={v} sx={{fontSize: '0.75rem'}}>{v}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="caption" sx={{color: 'text.secondary', whiteSpace: 'nowrap'}}>
                                {filteredClusters.length === 0
                                    ? '0 of 0'
                                    : `${clusterPage * clustersPerPage + 1}–${Math.min((clusterPage + 1) * clustersPerPage, filteredClusters.length)} of ${filteredClusters.length}${(clusterSearch || newOnly) ? ` (filtered from ${clusters.length})` : ''}`}
                            </Typography>
                            <IconButton
                                size="small"
                                disabled={clusterPage === 0}
                                onClick={(): void => setClusterPage((p) => p - 1)}
                            >
                                <Typography sx={{fontSize: '0.85rem'}}>◀</Typography>
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={(clusterPage + 1) * clustersPerPage >= filteredClusters.length}
                                onClick={(): void => setClusterPage((p) => p + 1)}
                            >
                                <Typography sx={{fontSize: '0.85rem'}}>▶</Typography>
                            </IconButton>
                        </Stack>
                    </Box>
                )}
                {clusters.length > 0 && filteredClusters.length === 0 && (
                    <Box sx={{px: 2, py: 4, textAlign: 'center'}}>
                        <Typography variant="caption" sx={{color: 'text.secondary'}}>
                            {clusterSearch
                                ? <>No clusters match &quot;{clusterSearch}&quot;{newOnly ? ` among those newly emerging in the last ${newClusterMonths} month${newClusterMonths !== 1 ? 's' : ''}` : ''}.</>
                                : newOnly
                                    ? `No newly-emerging clusters in the last ${newClusterMonths} month${newClusterMonths !== 1 ? 's' : ''}.`
                                    : 'No clusters match the current filters.'}
                        </Typography>
                    </Box>
                )}
                {filteredClusters.slice(clusterPage * clustersPerPage, (clusterPage + 1) * clustersPerPage).map((cluster, index) => {
                    const isExpanded = expandedClusters.has(cluster.signatureKey);
                    const globalIndex = clusterPage * clustersPerPage + index;
                    return (
                        <Paper
                            key={cluster.signatureKey}
                            data-cluster-key={cluster.signatureKey}
                            variant="outlined"
                            onMouseEnter={(): void => setHoveredClusterKey(cluster.signatureKey)}
                            onMouseLeave={(): void => setHoveredClusterKey((prev) => prev === cluster.signatureKey ? null : prev)}
                            sx={{
                                mb: 1.5,
                                // Hover sync — when the chart cursor sits on this cluster,
                                // tint the row so the user can find it without scrolling/searching.
                                ...(hoveredClusterKey === cluster.signatureKey ? {
                                    backgroundColor: 'action.hover',
                                    borderColor: 'primary.main',
                                    borderWidth: 2,
                                } : {}),
                                transition: 'background-color 80ms, border-color 80ms',
                            }}
                        >
                            {/* Cluster header */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1,
                                    cursor: 'pointer',
                                    '&:hover': {backgroundColor: 'action.hover'},
                                }}
                                onClick={(): void => toggleCluster(cluster.signatureKey, cluster.count)}
                            >
                                <IconButton size="small" sx={{mr: 1}}>
                                    {pendingExpandKey === cluster.signatureKey
                                        ? <CircularProgress size={16} disableShrink />
                                        : isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>

                                <Typography variant="subtitle2" sx={{mr: 1, whiteSpace: 'nowrap'}}>
                                Cluster {(clusterCountRank.get(cluster.signatureKey) ?? globalIndex) + 1} ({cluster.count} report{cluster.count !== 1 ? 's' : ''})
                                </Typography>
                                {((): React.ReactNode => {
                                    const stats = clusterTrendStats.get(cluster.signatureKey);
                                    if (!stats?.trend) return null;
                                    const statsLine = formatTrendStats(stats, trendWindowMonths);
                                    const headline = stats.trend === 'rising'
                                        ? (trendWindowMonths === 'midpoint'
                                            ? 'Accelerating — more reports in the second half of the date range than in the first'
                                            : `Accelerating — more reports in the last ${trendWindowMonths} months than in the prior ${trendWindowMonths} months`)
                                        : stats.trend === 'falling'
                                            ? (trendWindowMonths === 'midpoint'
                                                ? 'Decelerating — fewer reports in the second half of the date range than in the first'
                                                : `Decelerating — fewer reports in the last ${trendWindowMonths} months than in the prior ${trendWindowMonths} months`)
                                            : 'Stable — this failure pattern is occurring at a consistent rate';
                                    const title = statsLine ? (<><div>{headline}</div><div style={{marginTop: 4, opacity: 0.85}}>{statsLine}</div></>) : headline;
                                    const glyph = stats.trend === 'rising' ? '↑' : stats.trend === 'falling' ? '↓' : '→';
                                    const sx = stats.trend === 'rising'
                                        ? {fontSize: '0.8rem', fontWeight: 'bold', color: '#d32f2f', mr: 1, cursor: 'help'}
                                        : stats.trend === 'falling'
                                            ? {fontSize: '0.8rem', fontWeight: 'bold', color: '#2e7d32', mr: 1, cursor: 'help'}
                                            : {fontSize: '0.8rem', color: 'text.disabled', mr: 1, cursor: 'help'};
                                    return (
                                        <Tooltip title={title} arrow>
                                            <Typography sx={sx}>{glyph}</Typography>
                                        </Tooltip>
                                    );
                                })()}

                                {/* Barrier outcome chips */}
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                    {cluster.barriers.map((b) => (
                                        <Chip
                                            key={b.nodeId}
                                            label={b.uniqueId}
                                            size="small"
                                            icon={<CloseIcon sx={{fontSize: 14}} />}
                                            sx={{
                                                height: 22,
                                                fontSize: '0.7rem',
                                                backgroundColor: '#ffebee',
                                                color: '#c62828',
                                                borderColor: '#ef9a9a',
                                                '& .MuiChip-icon': {color: '#c62828'},
                                            }}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>

                            {/* Expandable report list */}
                            <Collapse in={isExpanded} unmountOnExit>
                                {((): React.ReactElement => {
                                    const page = clusterReportPage.get(cluster.signatureKey) ?? 0;
                                    const startIdx = page * clusterReportsPerPage;
                                    const pagedReports = cluster.reports.slice(startIdx, startIdx + clusterReportsPerPage);
                                    return (
                                        <Box sx={{px: 2, pb: 1}}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{fontWeight: 'bold', fontSize: '0.75rem', py: 0.5}}>Report ID</TableCell>
                                                        <TableCell sx={{fontWeight: 'bold', fontSize: '0.75rem', py: 0.5}}>Date</TableCell>
                                                        <TableCell sx={{fontWeight: 'bold', fontSize: '0.75rem', py: 0.5}}>Source</TableCell>
                                                        <TableCell align="center" sx={{fontWeight: 'bold', fontSize: '0.75rem', py: 0.5}}>Action</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {pagedReports.map((report) => (
                                                        <TableRow key={report.id}>
                                                            <TableCell sx={{fontSize: '0.75rem', py: 0.5}}>{report.uniqueId}</TableCell>
                                                            <TableCell sx={{fontSize: '0.75rem', py: 0.5}}>{formatDate(report.dateTime)}</TableCell>
                                                            <TableCell sx={{fontSize: '0.75rem', py: 0.5}}>{report.sourceName}</TableCell>
                                                            <TableCell align="center" sx={{py: 0.5}}>
                                                                <Tooltip title="View report details" arrow>
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{padding: '2px'}}
                                                                        onClick={(): void => navigateToReportAnalysis(report.id)}
                                                                    >
                                                                        <VisibilityIcon sx={{fontSize: 16}} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            <TablePagination
                                                component="div"
                                                count={cluster.reports.length}
                                                page={page}
                                                onPageChange={(_, newPage): void => {
                                                    setClusterReportPage((prev) => {
                                                        const next = new Map(prev);
                                                        next.set(cluster.signatureKey, newPage);
                                                        return next;
                                                    });
                                                }}
                                                rowsPerPage={clusterReportsPerPage}
                                                onRowsPerPageChange={(e): void => {
                                                    setClusterReportsPerPage(Number(e.target.value));
                                                    setClusterReportPage(new Map());
                                                }}
                                                rowsPerPageOptions={[10, 25, 50, 100]}
                                                labelRowsPerPage="Reports per page:"
                                                sx={{'& .MuiTablePagination-toolbar': {minHeight: 36}, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {fontSize: '0.75rem'}}}
                                            />
                                        </Box>
                                    );
                                })()}
                            </Collapse>
                        </Paper>
                    );
                })}
                {clusters.length > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{py: 1, px: 1, borderTop: 1, borderColor: 'divider', mt: 1}}
                    >
                        <Box sx={{flex: 1}} />
                        <Typography variant="caption" sx={{color: 'text.secondary'}}>
                        Clusters per page:
                        </Typography>
                        <FormControl size="small" sx={{minWidth: 60}}>
                            <Select
                                value={clustersPerPage}
                                onChange={(e): void => { setClustersPerPage(e.target.value as number); setClusterPage(0); }}
                                sx={{fontSize: '0.75rem', height: 28, '& .MuiSelect-select': {py: 0, px: 1}}}
                            >
                                {[5, 10, 25, 50].map((v) => (
                                    <MenuItem key={v} value={v} sx={{fontSize: '0.75rem'}}>{v}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" sx={{color: 'text.secondary'}}>
                            {filteredClusters.length === 0
                                ? '0 of 0'
                                : `${clusterPage * clustersPerPage + 1}–${Math.min((clusterPage + 1) * clustersPerPage, filteredClusters.length)} of ${filteredClusters.length}${(clusterSearch || newOnly) ? ` (filtered from ${clusters.length})` : ''}`}
                        </Typography>
                        <IconButton
                            size="small"
                            disabled={clusterPage === 0}
                            onClick={(): void => setClusterPage((p) => p - 1)}
                        >
                            <Typography sx={{fontSize: '0.85rem'}}>◀</Typography>
                        </IconButton>
                        <IconButton
                            size="small"
                            disabled={(clusterPage + 1) * clustersPerPage >= filteredClusters.length}
                            onClick={(): void => setClusterPage((p) => p + 1)}
                        >
                            <Typography sx={{fontSize: '0.85rem'}}>▶</Typography>
                        </IconButton>
                    </Stack>
                )}
            </>)}

            {/* Time-to-failure drill-down for a cluster (inter-occurrence gap PDF/CDF +
                opt-in Weibull overlay + Crow-AMSAA reliability growth). Opened by clicking
                a cluster in the Temporal Distribution chart. "Show in cluster list" runs the
                old chart→list navigation. */}
            <TimeToFailureDialog
                entity={gapDistCluster ? {
                    key: gapDistCluster.signatureKey,
                    label: `C${(clusterCountRank.get(gapDistCluster.signatureKey) ?? 0) + 1}`,
                    sublabel: gapDistCluster.barriers.map((b) => b.uniqueId).join(', '),
                } : null}
                entityNoun="cluster"
                eventNoun="occurrence"
                onClose={(): void => setGapDistCluster(null)}
                gapDistData={gapDistData}
                weibullFit={weibullFit}
                showWeibullFit={showClusterWeibull}
                onShowWeibullFitChange={setShowClusterWeibull}
                predictionDays={clusterPredictionDays}
                onPredictionDaysChange={setClusterPredictionDays}
                crowAmsaaFit={crowAmsaa.fit}
                initialTab={crowAmsaa.initialTab}
                onShowInList={(): void => {
                    const key = gapDistCluster?.signatureKey;
                    if (!key) return;
                    const masterIndex = clusters.findIndex((c) => c.signatureKey === key);
                    if (masterIndex < 0) return;
                    setChartFullscreen(false);
                    // The cluster list lives on the Clusters sub-tab; switch to it so the
                    // scroll target is mounted (the temporal chart is on the Charts sub-tab).
                    setActiveSubTab('clusters');
                    // Clear any active list search so the cluster is guaranteed to be on the
                    // visible page — otherwise it might be filtered out of the list view.
                    setClusterSearch('');
                    setClusterPage(Math.floor(masterIndex / clustersPerPage));
                    setExpandedClusters((prev) => {
                        if (prev.has(key)) return prev;
                        const next = new Set(prev);
                        next.add(key);
                        return next;
                    });
                    setScrollToClusterKey(key);
                    setGapDistCluster(null);
                }}
            />
        </Box>
    );
};

export default OccurrencePatternsTab;
